import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Html, useAnimations, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import type { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";

import { createPokeController, type ClawbertState } from "@/lib/pokeLines";

import type { Vector3Tuple } from "three";

const WALK_SPEED = 1.6;
const ARRIVE_RADIUS = 1.4;
const WANDER_RADIUS = 4.2;
const WANDER_INTERVAL_S = 9;

interface HeroLobsterProps {
  /** Wander destination; cleared on arrival. */
  walkTargetRef: React.MutableRefObject<Vector3Tuple | null>;
  onTap: () => void;
  onHoverStart?: (x: number, y: number) => void;
  onHoverEnd?: () => void;
  /** Small caption pinned above Clawbert when he has nothing else to say. */
  caption?: string;
  /** Drives which pool his poke lines come from. Defaults to idle. */
  pokeState?: ClawbertState;
}

/**
 * Clawbert for the landing hero. Same mesh and clips as the in-app agent,
 * with a self-contained idle-wander-walk loop instead of the engine store.
 */
export function HeroLobster({ walkTargetRef, onTap, onHoverStart, onHoverEnd, caption, pokeState = "idle" }: HeroLobsterProps) {
  const groupRef = useRef<THREE.Group>(null!);
  const headingRef = useRef(Math.PI * 0.75);
  const currentClipRef = useRef<string>("Idle_A");
  const wanderClockRef = useRef(3);

  const pokeRef = useRef(createPokeController());
  const [line, setLine] = useState<string | null>(null);
  const lineTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { scene: meshScene } = useGLTF("/assets/lobster/lobster-mesh.glb");
  const { animations } = useGLTF("/assets/lobster/lobster-animations.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(meshScene), [meshScene]);
  const { actions, mixer } = useAnimations(animations, clonedScene);

  const playClip = useCallback(
    (name: string, opts?: { once?: boolean }) => {
      const next = actions[name];
      if (!next || currentClipRef.current === name) return;
      const prev = actions[currentClipRef.current];
      prev?.fadeOut(0.3);
      next.reset().fadeIn(0.3).play();
      if (opts?.once) {
        next.setLoop(THREE.LoopOnce, 1);
        next.clampWhenFinished = true;
      } else {
        next.setLoop(THREE.LoopRepeat, Infinity);
      }
      currentClipRef.current = name;
    },
    [actions],
  );

  useEffect(() => {
    actions["Idle_A"]?.reset().fadeIn(0.2).play();
    const onFinished = () => playClip("Idle_A");
    mixer.addEventListener("finished", onFinished);
    return () => mixer.removeEventListener("finished", onFinished);
  }, [actions, mixer, playClip]);

  const handleTap = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      playClip("Clicked", { once: true });
      const said = pokeRef.current.poke(pokeState);
      setLine(said);
      if (lineTimer.current) clearTimeout(lineTimer.current);
      if (said) lineTimer.current = setTimeout(() => setLine(null), 3200);
      onTap();
    },
    [onTap, playClip, pokeState],
  );

  useEffect(() => () => { if (lineTimer.current) clearTimeout(lineTimer.current); }, []);

  useFrame((_, delta) => {
    const group = groupRef.current;
    if (!group) return;

    const target = walkTargetRef.current;
    if (target) {
      const to = new THREE.Vector3(target[0], 0, target[2]).sub(group.position);
      to.y = 0;
      const distance = to.length();
      if (distance <= ARRIVE_RADIUS) {
        walkTargetRef.current = null;
        playClip("Idle_A");
      } else {
        to.normalize();
        const step = Math.min(WALK_SPEED * delta, distance - ARRIVE_RADIUS + 0.01);
        group.position.addScaledVector(to, step);
        headingRef.current = THREE.MathUtils.lerp(
          headingRef.current,
          Math.atan2(to.x, to.z),
          0.08,
        );
        if (currentClipRef.current !== "Clicked") playClip("Walk");
      }
    } else if (currentClipRef.current === "Idle_A") {
      wanderClockRef.current -= delta;
      if (wanderClockRef.current <= 0) {
        wanderClockRef.current = WANDER_INTERVAL_S * (0.6 + Math.random() * 0.8);
        const angle = Math.random() * Math.PI * 2;
        const dist = 1.5 + Math.random() * WANDER_RADIUS;
        walkTargetRef.current = [Math.cos(angle) * dist, 0, Math.sin(angle) * dist];
      }
    }

    group.rotation.y = headingRef.current;
  });

  return (
    <group ref={groupRef} position={[0.8, 0, 0.2]} scale={2}>
      {/* Invisible hitbox — SkinnedMesh primitives are not raycastable in R3F */}
      <mesh
        position={[0, 0.5, 0]}
        onPointerDown={handleTap}
        onPointerEnter={(e) => {
          e.stopPropagation();
          document.body.style.cursor = "pointer";
          onHoverStart?.(e.nativeEvent.clientX, e.nativeEvent.clientY);
        }}
        onPointerLeave={() => {
          document.body.style.cursor = "";
          onHoverEnd?.();
        }}
      >
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      <primitive object={clonedScene} />

      {/* A poked line takes over the bubble; otherwise the caption shows. */}
      {(line ?? caption) && (
        <Html position={[0, 1.5, 0]} center zIndexRange={[20, 0]}>
          <p
            key={line ?? "caption"}
            className={`pointer-events-none w-max max-w-[230px] rounded-2xl px-3.5 py-2 text-center font-body text-[12px] leading-snug font-medium shadow-[0_8px_20px_-12px_rgba(33,31,28,0.45)] [animation:poke-pop_0.28s_ease-out] ${
              line
                ? "rounded-bl-md bg-home-ink text-home-cream"
                : "bg-home-cream/90 text-home-ink/80"
            }`}
          >
            {line ?? caption}
          </p>
        </Html>
      )}
    </group>
  );
}

useGLTF.preload("/assets/lobster/lobster-mesh.glb");
useGLTF.preload("/assets/lobster/lobster-animations.glb");
